import { type ComponentProps, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { cn, getGradientPair } from '../lib/utils'
import { ImageCardContext, useImageCardHasImage } from './imageCardContext'
import { clickableProps, type ActivateEvent } from '../lib/a11y'
import { focalPosition, type FocalAnchor } from '../lib/focalPosition'

interface ImageCardProps extends Omit<ComponentProps<typeof Card>, 'style'> {
  imageUrl?: string
  imageFocal?: FocalAnchor
  gradientSeed?: string
  onClick?: (e: ActivateEvent<HTMLDivElement>) => void
}

export function ImageCard({ imageUrl, imageFocal, gradientSeed, onClick, className, children, ...rest }: ImageCardProps) {
  const hasImage = !!imageUrl || !!gradientSeed
  let style: React.CSSProperties = {}
  if (imageUrl) {
    style = {
      backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: focalPosition(imageFocal, 'center'),
    }
  } else if (gradientSeed) {
    const [c1, c2] = getGradientPair(gradientSeed)
    style = {
      backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.75)), linear-gradient(135deg, ${c1}, ${c2})`,
    }
  }

  return (
    <ImageCardContext.Provider value={{ hasImage }}>
      <Card
        data-slot="image-card"
        style={style}
        {...clickableProps(onClick)}
        className={cn(
          'overflow-hidden transition-colors',
          onClick && 'cursor-pointer',
          hasImage ? 'border-primary/25 hover:border-primary/40' : 'hover:bg-accent/50',
          className,
        )}
        {...rest}
      >
        {children}
      </Card>
    </ImageCardContext.Provider>
  )
}

export function ImageCardHeader({ className, children, ...rest }: ComponentProps<typeof CardHeader>) {
  return (
    <CardHeader className={cn('pb-2', className)} {...rest}>
      <div className="flex items-start justify-between gap-2 min-w-0">{children}</div>
    </CardHeader>
  )
}

export function ImageCardTitle({ className, ...rest }: ComponentProps<typeof CardTitle>) {
  const hasImage = useImageCardHasImage()
  return <CardTitle className={cn('text-base truncate', hasImage && 'text-on-image', className)} {...rest} />
}

export function ImageCardContent({ className, ...rest }: ComponentProps<typeof CardContent>) {
  return <CardContent className={cn('space-y-1', className)} {...rest} />
}

interface ImageCardMetaProps {
  icon: LucideIcon
  children: ReactNode
  className?: string
}

export function ImageCardMeta({ icon: Icon, children, className }: ImageCardMetaProps) {
  const hasImage = useImageCardHasImage()
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 text-sm',
        hasImage ? 'text-on-image/80' : 'text-muted-foreground',
        className,
      )}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {children}
    </div>
  )
}
